import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import {
  fetchAuthSession,
  fetchUserAttributes,
  type FetchUserAttributesOutput,
} from 'aws-amplify/auth';
import { decode as atobPolyfill } from 'base-64';
import Constants from 'expo-constants';
import { Platform } from 'react-native';

import { handleRevokedToken } from './auth';

import type { FeatureFlagTraits } from '@/constants/featureFlags';
import { segmentClient } from '@/util/analytics';
import { isRevokedTokenError, parseAuthError } from '@/util/authTokens';
import { clearFeatureFlagIdentity, identifyFeatureFlagUser } from '@/util/featureFlags';
import { getFirstTouchAttribution } from '@/util/firstTouchAttribution';
import { Sentry } from '@/util/sentry';

/**
 * Cognito session utilities for authentication.
 *
 * Provides a unified interface to get the current user's session (tokens, claims, attributes).
 * Used by `auth.ts` for token-based API authentication and user profile loading.
 *
 * Key behavior:
 * - OAuth users: Attributes extracted from ID token claims (no API call).
 * - Email/password users: Attributes fetched from Cognito API.
 */

export interface CognitoIdTokenClaims {
  token_use: 'id';
  client_id?: string;
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  'custom:user_id'?: string;
  'custom:first_name'?: string;
  'custom:last_name'?: string;
  'custom:onboarding_status'?: string;
  linked_provider?: string;
  identities?: {
    userId: string;
    providerName: string;
    providerType: string;
    primary?: boolean;
    dateCreated?: string;
  }[];
  'cognito:username'?: string;
  'cognito:groups'?: string[];
  iat?: number;
  exp?: number;
  aud?: string;
  iss?: string;
  scope?: string;
}

/**
 * Parses JWT ID token claims from Cognito. Handles base64url encoding.
 */
export function parseIdTokenClaims(idToken: string): CognitoIdTokenClaims | null {
  try {
    const [, payloadPart] = idToken.split('.');

    if (!payloadPart) {
      return null;
    }

    const base64url = payloadPart.replace(/-/g, '+').replace(/_/g, '/');

    const padded = base64url + '==='.slice((base64url.length + 3) % 4);

    const decoded = isWeb ? atob(padded) : atobPolyfill(padded);

    return JSON.parse(decoded) as CognitoIdTokenClaims;
  } catch (error) {
    console.error('[session] Failed to parse ID token:', error);

    return null;
  }
}

/**
 * Determines if user authenticated via OAuth provider.
 */
export function isOAuthUser(claims: CognitoIdTokenClaims): boolean {
  return Array.isArray(claims.identities) && claims.identities.length > 0;
}

/**
 * Extracts user attributes from OAuth ID token claims.
 */
export function extractUserAttributesFromClaims(
  claims: CognitoIdTokenClaims
): FetchUserAttributesOutput {
  return {
    sub: claims.sub,
    email: claims.email,
    email_verified: claims.email_verified as any,
    name: claims.name,
    'custom:user_id': claims['custom:user_id'],
    'custom:first_name': claims['custom:first_name'] || claims.given_name,
    'custom:last_name': claims['custom:last_name'] || claims.family_name,
    given_name: claims.given_name,
    family_name: claims.family_name,
    'cognito:username': claims['cognito:username'],
    'cognito:groups': claims['cognito:groups'] as any,
  };
}

/**
 * Gets user attributes based on authentication method.
 */
async function getAttributesForSession(
  claims: CognitoIdTokenClaims | null,
  isOAuth: boolean
): Promise<FetchUserAttributesOutput> {
  if (claims && isOAuth) {
    return extractUserAttributesFromClaims(claims);
  }

  return await fetchUserAttributes();
}

/**
 * Gets the current user's active session (tokens, claims, attributes).
 *
 * Primary entry point for authentication state. Used by `auth.ts` to get ID tokens
 * for API requests and user attributes for profile loading.
 *
 * Returns session data or empty session if no user is authenticated.
 * Handles revoked tokens automatically.
 */
export async function getActiveUserSession() {
  try {
    const session = await fetchAuthSession();

    const idToken = session.tokens?.idToken?.toString() || null;

    if (!idToken) {
      return { idToken: null, claims: null, attributes: null, isOAuth: false };
    }

    const claims = parseIdTokenClaims(idToken);

    const isOAuth = claims ? isOAuthUser(claims) : false;

    const attributes = await getAttributesForSession(claims, isOAuth);

    return { idToken, claims, attributes, isOAuth };
  } catch (error) {
    const parsedError = parseAuthError(error);

    if (isRevokedTokenError(parsedError)) {
      await handleRevokedToken(parsedError, 'getActiveUserSession');
    }

    throw error;
  }
}

/**
 * User session initialization and cleanup utilities.
 *
 * These functions begin to consolidate session-aware service setup/teardown
 * to ensure consistent behavior across login and logout paths.
 *
 * @todo Set and clear global state using Jotai provider
 * @todo Initialize React Query cache with current user
 * @todo Load account linking context
 */

export type SessionUser = {
  userId: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  state?: string | null;
  userType?: string | null;
};

/**
 * Initializes a user session across all session-aware services.
 *
 * Called after successful authentication to set up:
 * - Feature user identity for feature flag targeting
 * - Segment analytics identity
 * - Sentry error tracking context
 *
 * @param user - The authenticated user's details
 */
export function initializeUserSession(user: SessionUser) {
  let { userId, email, firstName, lastName, state, userType } = user;

  email ||= 'unknown';

  firstName ||= 'Unknown';

  lastName ||= 'Unknown';

  userType ||= 'Unknown';

  const traits: FeatureFlagTraits = {
    userId,
    userType,
    email,
    stateAbbreviation: state || 'XX',
    platform: Platform.OS,
    appVersion: Constants.expoConfig?.version || 'Unknown',
  };

  void identifyFeatureFlagUser(userId, traits);

  const name = [firstName, lastName].filter(Boolean).join(' ');

  // Attach first-touch marketing attribution to the user's profile.
  const firstTouch = getFirstTouchAttribution();

  segmentClient.identify(userId, {
    email,
    firstName,
    lastName,
    name,
    state,
    userType,
    appVersion: Constants.expoConfig?.version,
    platform: Platform.OS,
    platformVersion: Platform.Version,
    ...(firstTouch ?? {}),
  });

  Sentry.setUser({ id: userId, email });
}

/**
 * Session teardown for genuine logout: clears feature-flag, analytics, and
 * error-tracking identity, and rotates the anonymous analytics ID so the next
 * person on a shared browser can't inherit it. Use only for real logout — for a
 * not-logged-in visitor, use the stale-identity clear instead, which preserves
 * the anonymous ID.
 *
 * Rotating also keeps the downstream data pipeline's identity resolution clean: it
 * links anonymous activity to a user by co-occurrence, so a reused anonymous ID
 * would otherwise surface as one ID shared across several users.
 */
export async function clearUserSession() {
  await clearFeatureFlagIdentity();

  segmentClient.reset();

  Sentry.setUser(null);
}

/**
 * Clears stale identity for a not-logged-in visitor WITHOUT rotating the anonymous
 * ID. Runs from the unauthenticated layout on every render with no logged-in user
 * (including visitors who never logged in); preserving the ID keeps a visitor's
 * pre-signup events under one ID — both in analytics and in the downstream data
 * pipeline's grouping, which would fragment if it churned. Genuine logout rotates
 * the ID instead.
 */
export async function clearStaleUserIdentity() {
  await clearFeatureFlagIdentity();

  segmentClient.reset(false);

  Sentry.setUser(null);
}
